import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { Calendar, X, CheckCircle2, Loader2, AlertTriangle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingManagePage = () => {
  const { token } = useParams();
  const [params] = useSearchParams();
  const action = params.get('action');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/booking/manage/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'This link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submitCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/booking/manage/${token}/cancel`);
      setDone({ type: 'cancelled', payload: res.data });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not cancel.');
    } finally { setSubmitting(false); }
  };

  const submitReschedule = async () => {
    if (!newDate || !newTime) { setError('Please pick a date and time first.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/booking/manage/${token}/reschedule`, {
        new_date: newDate, new_time: newTime,
      });
      setDone({ type: 'rescheduled', payload: res.data });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not reschedule.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-blue-600" size={28} /> <span className="ml-3 text-gray-700">Loading your booking…</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-md max-w-md w-full text-center border border-gray-200" data-testid="manage-error">
          <AlertTriangle className="mx-auto text-amber-500 mb-3" size={40} />
          <h2 className="text-xl font-semibold text-gray-900">Link not usable</h2>
          <p className="text-gray-600 mt-2">{error}</p>
          <a href="/" className="inline-block mt-5 px-4 py-2 bg-blue-600 text-white rounded-lg">Back to EnayaHub</a>
        </div>
      </div>
    );
  }

  if (done) {
    const heading = done.type === 'cancelled' ? 'Booking cancelled' : 'Booking rescheduled';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-md max-w-md w-full text-center border border-emerald-200" data-testid="manage-done">
          <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={40} />
          <h2 className="text-xl font-semibold text-gray-900">{heading}</h2>
          {done.type === 'rescheduled' && (
            <p className="text-gray-600 mt-2">New time: {done.payload.new_date} at {done.payload.new_time}</p>
          )}
          <p className="text-sm text-gray-500 mt-4">We just sent the updated details to your WhatsApp and email.</p>
          <a href="/" className="inline-block mt-5 px-4 py-2 bg-blue-600 text-white rounded-lg">Back to EnayaHub</a>
        </div>
      </div>
    );
  }

  const appt = data?.appointment || {};
  const resolvedAction = action || data?.action;

  return (
    <div className="min-h-screen bg-gray-50 p-4 flex items-start justify-center pt-12">
      <div className="bg-white p-8 rounded-2xl shadow-md max-w-lg w-full border border-gray-200" data-testid="manage-card">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Clock size={14} /> Link expires {data?.expires_at ? new Date(data.expires_at).toLocaleString() : 'soon'}
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {resolvedAction === 'cancel' ? 'Cancel your booking' : 'Reschedule your booking'}
        </h1>

        <div className="mt-5 bg-gray-50 rounded-xl p-4 text-sm space-y-1">
          {appt.reference_number && (
            <div className="pb-2 mb-2 border-b border-gray-200">
              <span className="text-gray-500">Reference:</span>{' '}
              <code className="bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-900 font-mono text-xs" data-testid="manage-reference-number">{appt.reference_number}</code>
            </div>
          )}
          <div><span className="text-gray-500">Service:</span> <b className="text-gray-900">{appt.service_name || '—'}</b></div>
          {appt.staff_name && <div><span className="text-gray-500">Staff:</span> {appt.staff_name}</div>}
          {appt.patient_name && <div><span className="text-gray-500">Customer:</span> {appt.patient_name}</div>}
          <div><span className="text-gray-500">Current time:</span> {appt.appointment_date} at {appt.appointment_time}</div>
          {appt.consultation_fee ? <div><span className="text-gray-500">Fee:</span> {appt.consultation_fee} KD</div> : null}
        </div>

        {error && <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

        {resolvedAction === 'reschedule' ? (
          <div className="mt-6 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New date</label>
              <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="w-full rounded-lg border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="manage-new-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New time</label>
              <input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)}
                className="w-full rounded-lg border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="manage-new-time" />
            </div>
            <button
              onClick={submitReschedule}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-3 rounded-lg font-medium"
              data-testid="manage-reschedule-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Calendar size={16} />} Confirm new time
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-gray-600">If you cancel, any paid amount will be refunded to your EnayaHub wallet per our cancellation policy.</p>
            <button
              onClick={submitCancel}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-3 rounded-lg font-medium"
              data-testid="manage-cancel-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />} Cancel booking
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingManagePage;
